/**
 * File downloads triggered via Blob URLs + programmatic <a click> often fail inside
 * React Native WebViews. When `window.ReactNativeWebView` is present, delegate to native
 * (see mobile-app/App.tsx SAVE_TEXT_FILE).
 */

declare global {
  interface Window {
    ReactNativeWebView?: { postMessage: (payload: string) => void };
  }
}

export function isNativeMiloraWebView(): boolean {
  return (
    typeof window !== 'undefined' && typeof window.ReactNativeWebView?.postMessage === 'function'
  );
}

/** UTF‑8-safe base64 (not limited to Latin-1 like raw `btoa`). */
function utf8ToBase64(text: string): string {
  const bytes = new TextEncoder().encode(text);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]!);
  }
  return btoa(binary);
}

function sanitizeFilename(name: string, fallback = 'download.csv'): string {
  const stripped = name.replace(/[/\\]/g, '_').replace(/\s+/g, '_').trim();
  const sliced = stripped.slice(0, 200);
  return sliced || fallback;
}

/**
 * Downloads a UTF-8 text file (CSV, etc.). Uses native bridge in Milora mobile WebView,
 * otherwise a browser Blob download.
 */
export function triggerTextFileDownload(
  filename: string,
  contents: string,
  mimeType = 'text/csv;charset=utf-8'
): void {
  const safeName = sanitizeFilename(filename, 'download.csv');

  if (isNativeMiloraWebView()) {
    window.ReactNativeWebView!.postMessage(
      JSON.stringify({
        type: 'SAVE_TEXT_FILE',
        filename: safeName,
        base64: utf8ToBase64(contents),
        mimeType: mimeType.split(';')[0] || 'text/csv',
      })
    );
    return;
  }

  const blob = new Blob([contents], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = safeName;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Same as building a Blob from binary response data then clicking — WebView-safe path included.
 */
export function triggerBlobFileDownload(blob: Blob, filename: string): void {
  const safeName = sanitizeFilename(filename, 'download.bin');

  if (isNativeMiloraWebView()) {
    void blob.arrayBuffer().then((buffer) => {
      const bytes = new Uint8Array(buffer);
      let binary = '';
      const chunkSize = 8192;
      for (let i = 0; i < bytes.length; i += chunkSize) {
        const chunk = bytes.subarray(i, i + chunkSize);
        for (let j = 0; j < chunk.length; j++) {
          binary += String.fromCharCode(chunk[j]!);
        }
      }
      const base64 = btoa(binary);
      window.ReactNativeWebView!.postMessage(
        JSON.stringify({
          type: 'SAVE_TEXT_FILE',
          filename: safeName,
          base64,
          mimeType: blob.type || 'application/octet-stream',
        })
      );
    });
    return;
  }

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = safeName;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
